import dayjs from "dayjs"

export const DATE_RANGE_FILTERS = [
  { id: 1, label: "All", value: "all" },
  { id: 2, label: "Today", value: "today" },
  { id: 3, label: "Yesterday", value: "yesterday" },
  { id: 4, label: "Last Week", value: "last-week" },
  { id: 5, label: "Last 7 days", value: "last-7-days" },
  { id: 6, label: "Last Month", value: "last-month" },
  { id: 7, label: "This Year", value: "this-year" },
  { id: 8, label: "Custom", value: "custom" },
] as const

export const DATE_RANGE_FILTER_GROUPS = [DATE_RANGE_FILTERS.slice(0, 4), DATE_RANGE_FILTERS.slice(4)] as const

const getDateRangeForValue = (value: string): { start: Date | null; end: Date | null } => {
  if (value === "today") {
    return { start: new Date(), end: null }
  }

  if (value === "yesterday") {
    const yesterday = new Date()
    yesterday.setDate(new Date().getDate() - 1)
    return { start: yesterday, end: yesterday }
  }

  if (value === "last-week") {
    const lastWeekStart = new Date()
    lastWeekStart.setDate(lastWeekStart.getDate() - 7 - lastWeekStart.getDay())
    const lastWeekEnd = new Date(lastWeekStart)
    lastWeekEnd.setDate(lastWeekEnd.getDate() + 6)
    return { start: lastWeekStart, end: lastWeekEnd }
  }

  if (value === "last-7-days") {
    const endDate = new Date()
    endDate.setDate(endDate.getDate() - 1)
    const startDate = new Date(endDate)
    startDate.setDate(startDate.getDate() - 6)
    return { start: startDate, end: endDate }
  }

  if (value === "last-month") {
    const lastMonthStart = new Date()
    lastMonthStart.setMonth(lastMonthStart.getMonth() - 1)
    lastMonthStart.setDate(1)
    const lastMonthEnd = new Date(lastMonthStart)
    lastMonthEnd.setMonth(lastMonthEnd.getMonth() + 1)
    lastMonthEnd.setDate(0)
    return { start: lastMonthStart, end: lastMonthEnd }
  }

  if (value === "this-year") {
    const thisYearStart = new Date()
    thisYearStart.setMonth(0)
    thisYearStart.setDate(1)
    return { start: thisYearStart, end: null }
  }

  return { start: null, end: null }
}

export const getDateRangeParams = (
  value: string,
  paramName: string,
  customStart?: Date | null,
  customEnd?: Date | null,
): Record<string, string | null> => {
  const startKey = `${paramName}_start`
  const endKey = `${paramName}_end`

  if (value === "custom" && customStart) {
    const startStr = dayjs(customStart).format("YYYY-MM-DD")
    const endStr = customEnd ? dayjs(customEnd).format("YYYY-MM-DD") : null
    return { [startKey]: startStr, [endKey]: endStr }
  }

  const { start, end } = getDateRangeForValue(value)
  const startStr = start ? dayjs(start).format("YYYY-MM-DD") : null
  const endStr = end ? dayjs(end).format("YYYY-MM-DD") : null
  return { [startKey]: startStr, [endKey]: endStr }
}

export const getFilterValueFromParams = (params: Record<string, string>, paramName: string): string => {
  const startKey = `${paramName}_start`
  const endKey = `${paramName}_end`
  const start = params[startKey] ?? null
  const end = params[endKey] ?? null

  if (!start && !end) return "all"

  const today = dayjs().format("YYYY-MM-DD")
  const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD")

  if (start === today && !end) return "today"
  if (start === yesterday && end === yesterday) return "yesterday"

  const last7DaysParams = getDateRangeParams("last-7-days", paramName)
  if (start === last7DaysParams[startKey] && end === last7DaysParams[endKey]) return "last-7-days"

  const lastWeekParams = getDateRangeParams("last-week", paramName)
  if (start === lastWeekParams[startKey] && end === lastWeekParams[endKey]) return "last-week"

  const lastMonthParams = getDateRangeParams("last-month", paramName)
  if (start === lastMonthParams[startKey] && end === lastMonthParams[endKey]) return "last-month"

  const thisYearParams = getDateRangeParams("this-year", paramName)
  if (start === thisYearParams[startKey] && !end) return "this-year"

  if (start && end) return "custom"
  return "all"
}
